import {
	GetCapsuleEntries,
	GetCapsuleEntriesByDate,
} from "@app/api/CapsuleEntries";
import {
	type EntryForDisplay,
	type EntryForHistory,
	type EntryResponse,
	UnMarshalContent,
} from "@app/interfaces/CapsuleEntry";
import type { ProjectSummary } from "@app/interfaces/ProjectSummary";
import { useQuery } from "@tanstack/react-query";

export const useEntriesForProject = ({
	project,
}: {
	project: ProjectSummary;
}) => {
	return useQuery({
		queryKey: ["entries", project.id],
		queryFn: async () => {
			const batchSize = 5;
			let firstPage = 1;
			const allEntries: EntryResponse[] = [];

			while (true) {
				const pages = await Promise.all(
					Array.from({ length: batchSize }, (_, index) =>
						GetCapsuleEntries(project.id, firstPage + index),
					),
				);
				const hasEmptyPage = pages.some(({ entries }) => entries.length === 0);

				allEntries.push(...pages.flatMap(({ entries }) => entries));

				if (hasEmptyPage) {
					break;
				}

				firstPage += batchSize;
			}

			return allEntries
				.filter(
					(f) =>
						f.type === "note" &&
						f.content != null &&
						f.content.substring(0, 13) === "Time Tracking",
				)
				.map((f) => {
					const content = UnMarshalContent(f.content);
					if (content == null) {
						return null;
					}
					const { activity, minutes, details, requisitioner, ispTag } = content;

					const h: EntryForDisplay = {
						id: f.id,
						project_id: project.id,
						project_ref: project.project_id,
						date: new Date(f.entryAt).toLocaleDateString(),
						time: new Date(f.entryAt).toLocaleTimeString(),
						consultant: (f.creator || { name: "" }).name,
						activity: activity,
						details: details,
						minutes: Number(minutes.toString()),
						requisitioner: requisitioner,
						ispTag: ispTag,
					};
					return h;
				})
				.filter((h): h is EntryForDisplay => h !== null);
		},
	});
};

export const useEntries = () => {
	const getEntriesByDate = useQuery({
		queryKey: ["entries-by-date"],
		queryFn: async () => {
			try {
				const batchSize = 3;
				const pages = await Promise.all(
					Array.from({ length: batchSize }, (_, index) =>
						GetCapsuleEntriesByDate(index + 1),
					),
				);
				const allEntries = pages.flatMap(({ entries }) => entries);

				return allEntries
					.filter(
						(f) =>
							f.type === "note" &&
							f.content != null &&
							f.content.substring(0, 13) === "Time Tracking",
					)
					.map((res) => {
						return {
							PartyID: res.kase?.party?.id || 0,
							PartyName: res.kase?.party?.name || "",
							ProjectID: res.kase?.id || 0,
							ProjectRef: res.kase?.name || "",
							ProjectDesc: res.kase?.description || "",
							ProjectStatus: res.kase?.status || "",
							CreatorID: res.creator?.id || 0,
							CreatorName: res.creator?.name || "",
							CreatedAt: res.createdAt,
							Content: res.content,
						} as EntryForHistory;
					});
			} catch (error) {
				console.error("Failed to fetch entries by date:", error);
				return [];
			}
		},
		staleTime: 0,
		retry: 1,
	});
	return getEntriesByDate;
};
